'use client';

/**
 * WebBottomCtaCard — closing CTA section.
 *
 * Ports klyde-website/src/pages/ForBuyers.tsx bottom CTA. Big heading
 * (with embedded \n for soft line break), primary pill button (typically
 * mailto), small fineprint, and an optional "email us:" line with mailto
 * link. Content via `web_for_buyers_bottom_cta` strategy.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebBottomCtaContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


interface WebBottomCtaCardProps {
  strategyAction?: string;
}

export function WebBottomCtaCard({
  strategyAction = 'web_for_buyers_bottom_cta',
}: WebBottomCtaCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebBottomCtaContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebBottomCtaContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load CTA');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  // Headline split on \n for soft line break (renderer policy; YAML
  // carries a single string with \n, we split here).
  const headlineLines = data.headline.split('\n');

  return (
    <section
      id="book-demo"
      className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden"
    >
      <motion.div
        className="container mx-auto text-center relative z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className={cn(T.name, 'mb-8')}>
          {headlineLines.map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </h2>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <a
            href={data.cta.href}
            className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-lg font-semibold text-primary-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 transition-all"
          >
            {data.cta.label}
          </a>
          {data.secondary_cta && (
            <a
              href={data.secondary_cta.href}
              className="inline-flex items-center justify-center rounded-full border border-border px-10 py-4 text-lg font-semibold text-foreground hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              {data.secondary_cta.label}
            </a>
          )}
        </div>

        {data.fineprint && (
          <p className={cn(T.metadata, 'mb-8')}>{data.fineprint}</p>
        )}

        {(data.email_label || data.email) && (
          <p className={T.metadata}>
            {data.email_label}
            {data.email_label && data.email ? ' ' : null}
            {data.email && (
              <a
                href={`mailto:${data.email}`}
                className="text-primary hover:underline"
              >
                {data.email}
              </a>
            )}
          </p>
        )}
      </motion.div>
    </section>
  );
}
